import React, { useEffect, useCallback, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { useSidebar } from '../../context/SidebarContext';
import { FaChevronDown, FaTh, FaEllipsisH, FaNewspaper, FaScroll, FaUsers, FaDumbbell, FaCalendarAlt, FaEuroSign, FaImages, FaUserShield } from 'react-icons/fa'
import { Link } from 'react-router-dom';

type NavItem = {
  name: string;
  icon: React.ReactNode;
  path?: string;
  subItems?: { name: string; path: string; pro?: boolean; new?: boolean }[];
};

const navItems: NavItem[] = [
  {
    icon: <FaTh />,
    name: 'Dashboard',
    path: '/admin/dashboard',
  },
  {
    icon: <FaNewspaper />,
    name: 'Nieuws',
    subItems: [
        { name: 'Nieuwsberichten', path: '/admin/nieuwsberichten' },
        { name: 'Nieuwsbrief', path: '/admin/nieuwsbrief'}
    ]
  },
  {
    icon: <FaUsers />,
    name: 'Bestuur',
    path: '/admin/bestuur',
  },
  {
    icon: <FaDumbbell />,
    name: 'Trainingen',
    subItems: [
      { name: 'Zomer', path: '/admin/trainingen/zomer' },
      { name: 'Winter', path: '/admin/trainingen/winter'}
    ]
  },
  {
    icon: <FaCalendarAlt />,
    name: 'Calendar',
    subItems: [
      { name: 'Robin Hood Boechout', path: '/admin/calendar/robin-hood-boechout' },
      { name: 'Luchtdoelschieten', path: '/admin/calendar/luchtdoelschieten' },
      { name: 'Antwerpse Federatie', path: '/admin/calendar/antwerpse-federatie' },
    ]
  },
  {
    icon: <FaEuroSign />,
    name: 'Lidgeld',
    path: '/admin/lidgeld',
  },
  {
    icon: <FaImages />,
    name: 'Foto\'s',
    path: '/admin/fotos',
  },
  {
    icon: <FaUserShield />,
    name: 'Gebruikers',
    path: '/admin/gebruikers',
  }
];

const othersItems: NavItem[] = [
  {
    icon: <FaScroll />,
    name: 'Club Reglement',
    path: '/admin/legal/reglement',
  }
];

const Sidebar: React.FC = () => {
  const { isExpanded, isMobileOpen, isHovered, setIsHovered } = useSidebar();
  const location = useLocation();

  const [openSubmenu, setOpenSubmenu] = useState<{
    type: 'main' | 'others';
    index: number;
  } | null>(null);
  const [subMenuHeight, setSubMenuHeight] = useState<Record<string, number>>(
      {}
  );
  const subMenuRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const isActive = useCallback(
      (path: string) => location.pathname === path,
      [location.pathname]
  );

  useEffect(() => {
    let submenuMatched = false;
    ['main', 'others'].forEach((menuType) => {
      const items = menuType === 'main' ? navItems : othersItems;
      items.forEach((nav, index) => {
        if (nav.subItems) {
          nav.subItems.forEach((subItem) => {
            if (isActive(subItem.path)) {
              setOpenSubmenu({
                type: menuType as 'main' | 'others',
                index,
              });
              submenuMatched = true;
            }
          });
        }
      });
    });

    if (!submenuMatched) {
      setOpenSubmenu(null);
    }
  }, [location, isActive]);

  useEffect(() => {
    if (openSubmenu !== null) {
      const key = `${openSubmenu.type}-${openSubmenu.index}`;
      if (subMenuRefs.current[key]) {
        setSubMenuHeight((prevHeights) => ({
          ...prevHeights,
          [key]: subMenuRefs.current[key]?.scrollHeight || 0,
        }));
      }
    }
  }, [openSubmenu]);

  const handleSubmenuToggle = (index: number, menuType: 'main' | 'others') => {
    setOpenSubmenu((prevOpenSubmenu) => {
      if (
          prevOpenSubmenu &&
          prevOpenSubmenu.type === menuType &&
          prevOpenSubmenu.index === index
      ) {
        return null;
      }
      return { type: menuType, index };
    });
  };

  const renderMenuItems = (items: NavItem[], menuType: 'main' | 'others') => (
      <ul className='flex flex-col gap-4'>
        {items.map((nav, index) => (
            <li key={nav.name}>
              {nav.subItems ? (
                  <button
                      onClick={() => handleSubmenuToggle(index, menuType)}
                      className={`menu-item group ${
                          openSubmenu?.type === menuType && openSubmenu?.index === index
                              ? 'menu-item-active'
                              : 'menu-item-inactive'
                      } cursor-pointer ${
                          !isExpanded && !isHovered
                              ? 'lg:justify-center'
                              : 'lg:justify-start'
                      }`}
                  >
              <span className={`${ openSubmenu?.type === menuType && openSubmenu?.index === index ? 'menu-item-icon-active' : 'menu-item-icon-inactive' }`}>
                {nav.icon}
              </span>
                    {(isExpanded || isHovered || isMobileOpen) && (
                        <span className='menu-item-text'>{nav.name}</span>
                    )}
                    {(isExpanded || isHovered || isMobileOpen) && (
                        <FaChevronDown
                            className={`ml-auto w-5 h-5 transition-transform duration-200 ${
                                openSubmenu?.type === menuType &&
                                openSubmenu?.index === index
                                    ? 'rotate-180 text-brand-500'
                                    : ''
                            }`}
                        />
                    )}
                  </button>
              ) : (
                  nav.path && (
                      <Link to={nav.path} className={`menu-item group ${ isActive(nav.path) ? 'menu-item-active' : 'menu-item-inactive' }`}>
                        <span className={`${ isActive(nav.path) ? 'menu-item-icon-active' : 'menu-item-icon-inactive' }`} >
                          {nav.icon}
                        </span>
                        {(isExpanded || isHovered || isMobileOpen) && (
                            <span className='menu-item-text'>{nav.name}</span>
                        )}
                      </Link>
                  )
              )}
              {nav.subItems && (isExpanded || isHovered || isMobileOpen) && (
                  <div
                      ref={(el) => {
                        subMenuRefs.current[`${menuType}-${index}`] = el;
                      }}
                      className='overflow-hidden transition-all duration-300'
                      style={{
                        height:
                            openSubmenu?.type === menuType && openSubmenu?.index === index
                                ? `${subMenuHeight[`${menuType}-${index}`]}px`
                                : '0px',
                      }}>
                    <ul className='mt-2 space-y-1 ml-9'>
                      {nav.subItems.map((subItem) => (
                          <li key={subItem.name}>
                            <Link to={subItem.path} className={`menu-dropdown-item ${ isActive(subItem.path) ? 'menu-dropdown-item-active' : 'menu-dropdown-item-inactive' }`}>
                              {subItem.name}
                            </Link>
                          </li>
                      ))}
                    </ul>
                  </div>
              )}
            </li>
        ))}
      </ul>
  );

  return (
      <aside className={`fixed mt-16 flex flex-col lg:mt-0 top-0 px-5 left-0 bg-white text-gray-900 h-screen transition-all duration-300 ease-in-out z-50 border-r border-gray-200 
        ${ isExpanded || isMobileOpen ? 'w-[290px]' : isHovered ? 'w-[290px]' : 'w-[90px]' }
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
          onMouseEnter={() => !isExpanded && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)} >
        <div className={`py-8 flex ${ !isExpanded && !isHovered ? 'lg:justify-center' : 'justify-start' }`}></div>
        <div className='flex flex-col overflow-y-auto duration-300 ease-linear no-scrollbar'>
          <nav className='mb-6'>
            <div className='flex flex-col gap-4'>
              <div>
                <h2 className={`mb-4 text-xs uppercase flex leading-[20px] text-gray-400 ${ !isExpanded && !isHovered ? 'lg:justify-center' : 'justify-start' }`}>
                  {isExpanded || isHovered || isMobileOpen ? (
                      'Menu'
                  ) : (
                      <FaEllipsisH />
                  )}
                </h2>
                {renderMenuItems(navItems, 'main')}
              </div>

              <div>
                <h2 className={`mb-4 text-xs uppercase flex leading-[20px] text-gray-400 ${ !isExpanded && !isHovered ? 'lg:justify-center' : 'justify-start' }`}>
                  {isExpanded || isHovered || isMobileOpen ? (
                      'Legal'
                  ) : (
                      <FaEllipsisH />
                  )}
                </h2>
                {renderMenuItems(othersItems, 'others')}
              </div>
            </div>
          </nav>
        </div>
      </aside>
  );
};

export default Sidebar;